/**
 * Mobile Bestellseite für Gäste
 * 
 * Öffentlich zugänglich - kein Login erforderlich
 * Optimiert für Smartphones
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  ShoppingCart, Plus, Minus, X, Send, Check, AlertCircle,
  ChevronRight, Loader2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function GuestOrderPage() {
  const { tenantId, tableId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  
  // Data
  const [restaurantName, setRestaurantName] = useState('');
  const [tableName, setTableName] = useState('');
  const [menu, setMenu] = useState([]);
  const [settings, setSettings] = useState({});
  
  // Cart
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    loadMenu();
  }, [tenantId, tableId]);

  const loadMenu = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Tisch-Info laden
      const tableRes = await fetch(`${BACKEND_URL}/api/qr-order/table/${tenantId}/${tableId}`);
      if (tableRes.ok) {
        const tableData = await tableRes.json();
        setTableName(tableData.table_name);
      }
      
      // Menü laden
      const menuRes = await fetch(`${BACKEND_URL}/api/qr-order/menu/${tenantId}`);
      if (!menuRes.ok) {
        throw new Error('Menü konnte nicht geladen werden');
      }
      
      const menuData = await menuRes.json();
      setRestaurantName(menuData.restaurant_name);
      setMenu(menuData.menu || []);
      setSettings(menuData.settings || {});
      
    } catch (err) {
      console.error('Load error:', err);
      setError('Menü konnte nicht geladen werden. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Cart Functions
  const addToCart = (product) => {
    const existingIndex = cart.findIndex(item => item.product_id === product.product_id);
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, {
        product_id: product.product_id,
        product_name: product.name,
        price: product.price,
        quantity: 1,
        notes: '',
        removed_ingredients: []
      }]);
    }
    
    toast.success(`${product.name} hinzugefügt`);
  };

  const updateQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Submit Order
  const submitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Ihr Warenkorb ist leer');
      return;
    }
    
    if (settings.require_name && !customerName.trim()) {
      toast.error('Bitte geben Sie Ihren Namen ein');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/qr-order/submit/${tenantId}/${tableId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table_id: tableId,
          items: cart,
          customer_name: customerName || null,
          customer_note: customerNote || null
        })
      });
      
      if (!response.ok) {
        throw new Error('Bestellung konnte nicht aufgegeben werden');
      }
      
      const result = await response.json();
      
      setOrderSuccess(true);
      setCart([]);
      setCartOpen(false);
      
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Fehler beim Absenden der Bestellung');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-500">Menü wird geladen...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-700 mb-4">{error}</p>
          <Button onClick={loadMenu}>Erneut versuchen</Button>
        </div>
      </div>
    );
  }

  // Success State
  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-emerald-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Bestellung aufgegeben!
          </h1>
          <p className="text-gray-600 mb-6">
            Ihre Bestellung wird zubereitet und in Kürze serviert.
          </p>
          <Button 
            onClick={() => {
              setOrderSuccess(false);
              loadMenu();
            }}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            Weitere Bestellung aufgeben
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">{restaurantName}</h1>
          <p className="text-sm text-gray-500">{tableName}</p>
          {settings.custom_message && (
            <p className="text-sm text-blue-600 mt-1">{settings.custom_message}</p>
          )}
        </div>
      </header>

      {/* Menu */}
      <main className="px-4 py-4 space-y-6">
        {menu.map((category) => (
          <div key={category.category_id}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {category.name}
            </h2>
            <div className="space-y-3">
              {category.products.map((product) => (
                <Card key={product.product_id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{product.name}</h3>
                        {product.description && (
                          <p className="text-sm text-gray-500 mt-1">{product.description}</p>
                        )}
                        {settings.show_prices && (
                          <p className="text-lg font-bold text-emerald-600 mt-2">
                            €{product.price?.toFixed(2)}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => addToCart(product)}
                        className="bg-emerald-500 hover:bg-emerald-600 ml-4"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

        {menu.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Keine Produkte verfügbar</p>
          </div>
        )}
      </main>

      {/* Cart Button (Fixed) */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg">
          <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetTrigger asChild>
              <Button className="w-full bg-emerald-500 hover:bg-emerald-600 h-14 text-lg">
                <ShoppingCart className="w-5 h-5 mr-2" />
                Warenkorb ({cartCount})
                <span className="ml-auto">€{cartTotal.toFixed(2)}</span>
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </SheetTrigger>
            
            <SheetContent side="bottom" className="h-[80vh] rounded-t-xl">
              <SheetHeader>
                <SheetTitle>Ihre Bestellung</SheetTitle>
              </SheetHeader>
              
              <div className="mt-4 space-y-4 overflow-y-auto max-h-[50vh]">
                {cart.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-gray-500">€{item.price?.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(index, -1)}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(index, 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(index)}
                        className="text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Name & Note */}
              <div className="mt-4 space-y-3">
                {settings.require_name && (
                  <Input
                    placeholder="Ihr Name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                )}
                <Textarea
                  placeholder="Anmerkungen zur Bestellung (optional)"
                  value={customerNote}
                  onChange={(e) => setCustomerNote(e.target.value)}
                  rows={2}
                />
              </div>
              
              {/* Total & Submit */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-medium">Gesamt</span>
                  <span className="text-2xl font-bold text-emerald-600">
                    €{cartTotal.toFixed(2)}
                  </span>
                </div>
                <Button
                  className="w-full h-14 text-lg bg-emerald-500 hover:bg-emerald-600"
                  onClick={submitOrder}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Bestellung aufgeben
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      )}
    </div>
  );
}
